<template>
  <div class="pl-page">
    <pro-list-frame
      v-if="isDesktopListLayout"
      ref="plTable"
      :title="$t($route.name)"
      icon="mdi-calendar-alert"
      :total="totalItems"
      :rows="items.length"
      :search.sync="search"
      :columns="toggleableHeaders"
      :hidden="hiddenColumns"
      :chips="activeFilterChips"
      :filter-count="activeFilterChips.length"
      :selected-count="(selected || []).length"
      :loading="loading"
      :cell-sel="cellSel"
      :fixed="!mobileShow"
      @refresh="getItems"
      @print="printStudio = true"
      @filter="filter = true"
      @toggle-column="toggleColumn"
      @show-all-columns="hiddenColumns = []"
      @clear-chip="clearFilterChip"
      @clear-filters="resetFilters"
      @cell-down="onCellDown"
      @cell-over="onCellOver"
      @clear-cells="clearCellSel"
      @clear-selection="selected = []"
    >
      <template #bulk>
        <button
          type="button"
          class="pl-bulk__btn pl-bulk__btn--danger"
          @click="deletDialog = true"
        >
          <v-icon size="17">mdi-trash-can-outline</v-icon>
          {{ $t("remove") }}
        </button>
      </template>

      <template #kpis>
        <div class="pl-kpi pl-kpi--green">
          <span class="pl-kpi__icon"><v-icon size="17">mdi-login-variant</v-icon></span>
          <div class="pl-kpi__body">
            <span class="pl-kpi__label">{{ $t("in") }}</span>
            <span class="pl-kpi__value pl-num">{{ faNum($numberFormat(totals.in, "number")) }}</span>
          </div>
        </div>
        <div class="pl-kpi pl-kpi--rose">
          <span class="pl-kpi__icon"><v-icon size="17">mdi-logout-variant</v-icon></span>
          <div class="pl-kpi__body">
            <span class="pl-kpi__label">{{ $t("out") }}</span>
            <span class="pl-kpi__value pl-num">{{ faNum($numberFormat(totals.out, "number")) }}</span>
          </div>
        </div>
        <div class="pl-kpi pl-kpi--blue">
          <span class="pl-kpi__icon"><v-icon size="17">mdi-cube-outline</v-icon></span>
          <div class="pl-kpi__body">
            <span class="pl-kpi__label">{{ $t("remain") }}</span>
            <span class="pl-kpi__value pl-num">{{ faNum(formatRemain(totals.in - totals.out)) }}</span>
          </div>
        </div>
      </template>

      <v-data-table
        v-if="headers"
        ref="listTable"
        v-column-resize="expireDateItemsColumnPlugins"
        v-column-drag="expireDateItemsColumnPlugins"
        v-column-pin="expireDateItemsColumnPlugins"
        v-model="selected"
        :height="mobileShow ? 323 : plTableH"
        fixed-header
        :headers="tableHeaders"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="false"
        :hide-default-header="isMobile || mobileShow"
        multi-sort
        :page.sync="page"
        dense
        :show-select="showSelect"
        item-key="id"
        :footer-props="plFooterProps"
      >
        <template v-slot:no-data>
          <div class="pl-empty">
            <p>{{ $t("no-data") }}</p>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="pl-action-cell">
            <span class="pl-action-cell__row pl-num">
              <a @click="canOpenDoc(item) && edit(item)">{{
                faNum(parseInt(items.indexOf(item)) + parseInt(from || 1))
              }}</a>
            </span>
          </div>
        </template>

        <template v-slot:item.product.name="{ item }">
          <span class="pl-key">{{
            item.product && item.product.name ? item.product.name : $t("haveNot")
          }}</span>
        </template>

        <template v-slot:item.head.type="{ item }">
          <template v-if="item.head">
            <button
              v-if="canOpenDoc(item)"
              type="button"
              class="pl-key"
              @click="edit(item)"
            >
              {{ $t(item.head.type) }} {{ $t("number") }}: {{ faNum(item.head.id) }}
            </button>
            <span v-else class="pl-muted">
              {{ $t(item.head.type) }} {{ $t("number") }}: {{ faNum(item.head.id) }}
            </span>
            <button
              v-if="item.head.invoice"
              type="button"
              class="pl-pill pl-pill--violet"
              @click="invoice(item)"
            >
              {{ $t(item.head.invoice.type) }} · {{ faNum(item.head.invoice.number) }}
              <template v-if="item.head.invoice.customer">
                · {{ item.head.invoice.customer.name }}
              </template>
            </button>
            <button
              v-else-if="item.head.production"
              type="button"
              class="pl-pill pl-pill--green"
              @click="invoice(item)"
            >
              {{ $t(item.head.production.type) }} · {{ faNum(item.head.production.number) }}
            </button>
          </template>
          <span v-else class="pl-muted">{{ $t("haveNot") }}</span>
        </template>

        <template v-slot:item.head.date="{ item }">
          <span class="pl-num">{{
            item.head && item.head.date ? faNum(item.head.date) : $t("haveNot")
          }}</span>
        </template>

        <template v-slot:item.entrepot.name="{ item }">
          <span class="pl-ellipsis">{{
            item.entrepot && item.entrepot.name ? item.entrepot.name : $t("haveNot")
          }}</span>
        </template>

        <template v-slot:item.expire_date="{ item }">
          <span
            class="pl-pill"
            :class="expirePillClass(item.expire_date)"
          >
            {{ item.expire_date ? faNum(item.expire_date) : $t("haveNot") }}
          </span>
        </template>

        <template v-slot:item.in="{ item }">
          <span class="pl-amount pl-num">{{ faNum($numberFormat(item.in || 0, "number")) }}</span>
        </template>

        <template v-slot:item.out="{ item }">
          <span class="pl-amount pl-num">{{
            faNum($numberFormat(rowOut(item), "number"))
          }}</span>
        </template>

        <template v-slot:item.remain="{ item }">
          <span class="pl-amount pl-num">{{
            faNum(formatRemain(rowRemain(item)))
          }}</span>
        </template>

        <template v-slot:item.unit.fame="{ item }">
          <span class="pl-muted">{{
            item.unit && item.unit.fame ? item.unit.fame : $t("haveNot")
          }}</span>
        </template>

        <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
          {{ faNum(pageStart) }}–{{ faNum(pageStop) }} {{ $t("of") }}
          {{ faNum($numberFormat(itemsLength)) }}
        </template>

        <template v-slot:footer.prepend>
          <div
            v-if="!isMobile && !mobileShow"
            class="app-list-page-totals"
          >
            <span class="app-list-page-totals__label">{{ $t("sum") }}</span>
            <span class="app-list-page-totals__item">
              <span class="app-list-page-totals__key">{{ $t("in") }}</span>
              <span class="app-list-page-totals__val">
                {{ $numberFormat(totals.in, "number") }}
              </span>
            </span>
            <span class="app-list-page-totals__item">
              <span class="app-list-page-totals__key">{{ $t("out") }}</span>
              <span class="app-list-page-totals__val">
                {{ $numberFormat(totals.out, "number") }}
              </span>
            </span>
            <span class="app-list-page-totals__item app-list-page-totals__item--final">
              <span class="app-list-page-totals__key">{{ $t("remain") }}</span>
              <span class="app-list-page-totals__val">
                {{ formatRemain(totals.in - totals.out) }}
              </span>
            </span>
          </div>
          <div class="pl-goto">
            <span>{{ $t("goToPage") }}</span>
            <input
              v-model.number="page"
              type="number"
              min="1"
              :max="pageCount"
              class="pl-goto__input pl-num"
            />
            <span class="pl-num">/ {{ faNum(pageCount) }}</span>
          </div>
        </template>

        <template v-slot:body.append>
          <tr
            v-if="isMobile || mobileShow"
            :class="{ 'v-data-table__mobile-table-row': isMobile }"
          >
            <td
              :colspan="isMobile ? 6 : 6"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            >
              <strong>{{ $t("sum") }}</strong>
            </td>
            <td
              :colspan="isMobile ? 6 : 1"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            >
              {{ isMobile ? $t("in") + ":" : "" }}
              {{ $numberFormat(totals.in, "number") }}
            </td>
            <td
              :colspan="isMobile ? 6 : 1"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            >
              {{ isMobile ? $t("out") + ":" : "" }}
              {{ $numberFormat(totals.out, "number") }}
            </td>
            <td
              :colspan="isMobile ? 6 : 1"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            >
              {{ $t("remain") }}:
              {{ formatRemain(totals.in - totals.out) }}
            </td>
            <td
              :colspan="isMobile ? 6 : 2"
              :class="{ 'v-data-table__mobile-row': isMobile }"
            />
          </tr>
        </template>
      </v-data-table>
    </pro-list-frame>

    <pro-list-mobile
      v-else
      :title="$t($route.name)"
      :items="items"
      :total="totalItems"
      :loading="loading"
      :page="plPage"
      :page-count="pageCount || 1"
      :search.sync="search"
      :filterable="true"
      :printable="true"
      :card="plAutoCard"
      :actions="plAutoActions"
      :summary="mobileSummary"
      @load-more="plLoadPage"
      @refresh="plLoadPage(1)"
      @print="printStudio = true"
      @filter="filter = true"
    />

    <print-studio
      v-model="printStudio"
      :report="plReportKey"
      :title="$t($route.name)"
      :columns="plPrintCols"
      :source="plPrintSource"
      @error="serverErrorHandeler"
    />

    <v-dialog
      v-model="filter"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="720"
      scrollable
      content-class="acf-wrap"
    >
      <div class="acf acf--compact" :style="proListRootStyle" dir="rtl">
        <header class="acf-head">
          <span class="acf-head__icon">
            <v-icon size="18">mdi-filter-variant</v-icon>
          </span>
          <div class="acf-head__titles">
            <div class="acf-head__title">{{ $t("expireDateItemsFilterTitle") }}</div>
            <div class="acf-head__sub">
              <template v-if="activeFilterChips.length">
                <span class="acf-badge">{{ faNum(activeFilterChips.length) }}</span>
                {{ $t("accFilterActive") }}
              </template>
              <template v-else>{{ $t("accFilterNone") }}</template>
            </div>
          </div>
          <button type="button" class="acf-x" :title="$t('close')" @click="filter = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>

        <div class="acf-body">
          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-calendar-range</v-icon>
              {{ $t("expireDateRang") }}
            </div>
            <div class="acf-grid acf-grid--2">
              <div class="acf-date">
                <v-text-field
                  id="expireDate"
                  v-model="expireDate"
                  :label="$t('expireDateRang')"
                  clear-icon="flaticon2-close-cross"
                  outlined
                  hide-details
                  dense
                  clearable
                />
                <date-picker
                  v-model="expireDate"
                  element="expireDate"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                  range
                  clearable
                />
              </div>
            </div>
          </section>

          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-warehouse</v-icon>
              {{ $t("entrepotFilterMain") }}
            </div>
            <div class="acf-grid acf-grid--3">
              <autocomplete
                v-model="product"
                type="product"
                :title="$t('product')"
                :outlined="true"
              />
              <autocomplete
                v-model="entrepot"
                :title="$t('entrepot')"
                type="entrepot"
                :outlined="true"
                :multiple="true"
                :filter="true"
              />
              <autocomplete
                v-model="branch"
                title="branch"
                type="branch"
                :filter="true"
                :multiple="true"
              />
              <v-select
                v-model="selectedHeaders"
                :items="headers"
                :label="$t('sellFilterColumns')"
                dense
                outlined
                multiple
                return-object
                hide-details
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0" class="acf-sel">{{ item.text }}</span>
                  <span v-if="index === 1" class="acf-sel acf-sel--more">
                    +{{ faNum(selectedHeaders.length - 1) }}
                  </span>
                </template>
              </v-select>
            </div>
          </section>
        </div>

        <footer class="acf-foot">
          <button type="button" class="acf-btn acf-btn--primary" @click="applyFilters">
            <v-icon size="16">mdi-filter-check-outline</v-icon>
            {{ $t("accFilterApply") }}
          </button>
          <button
            type="button"
            class="acf-btn acf-btn--ghost"
            :disabled="!activeFilterChips.length"
            @click="resetFilters"
          >
            <v-icon size="16">mdi-filter-remove-outline</v-icon>
            {{ $t("accDocListClearFilters") }}
          </button>
          <span class="acf-spacer" />
          <button type="button" class="acf-btn" @click="filter = false">
            {{ $t("cancel") }}
          </button>
        </footer>
      </div>
    </v-dialog>

    <v-dialog
      v-model="deletDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="400"
    >
      <v-card>
        <v-col>
          <v-btn icon class="close-hover" @click="deletDialog = false">
            <v-icon color="grey" large>mdi-close</v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <v-img class="mx-auto" width="200" src="@/assets/stop.svg" />
        </v-col>
        <v-col class="mt-3 pr-7">
          <p class="mx-auto font-weight-medium">
            {{ $t("are-you-sure-delete-this-case") }}
          </p>
        </v-col>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="6">
              <v-btn
                v-if="selected.length > 0"
                color="primary"
                @click="removegroup"
              >
                {{ $t("remove") }}
              </v-btn>
              <v-btn
                v-else
                color="primary"
                @click="remove"
              >
                {{ $t("remove") }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="grey"
                outlined
                class="float-left"
                @click="deletDialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <snakbar
      v-model="snakbar"
      :text="snakbarText"
      :active="snakbar"
      :color="snakbarColor"
    />
  </div>
</template>

<script>
import General from "../../../../Mixins/general";
import autocomplete from "../../components/general/patial/autocomplete";
import {
  applyColumnSettings,
  loadColumnSettings,
} from "@/plugins/columnSettings/columnSettings";
import { reorderPinnedHeaders } from "@/plugins/columnPin/columnPin";
import ProList from "@/Mixins/proList";
import ProListFrame from "@/components/proList/ProListFrame.vue";
import ProListMobile from "@/components/proList/ProListMobile.vue";
import PrintStudio from "@/modules/print/PrintStudio.vue";

export default {
  name: "ExpireDateItems",
  components: {
    ProListFrame,
    ProListMobile,
    PrintStudio,
    Snakbar: () => import("../../components/general/snakbar/snakbar"),
    autocomplete,
  },
  mixins: [General, ProList],
  props: ["input", "refId", "productProp", "entrepotProp", "idProp", "mobileShow"],
  data: () => ({
    selected: [],
    pageCount: 1,
    page: 1,
    deletDialog: false,
    filter: false,
    sumAmount: 0,
    sumCount: 0,
    expireDate: null,
    entrepot: null,
    product: null,
    branch: null,
    productStatus: null,
    productStatusItems: null,
    model: null,
    from: 1,
    tag: null,
    temp: null,
    deleteItem: null,
    items: [],
    sortBy: [],
    dialog: null,
    timer: 0,
    totalItems: 0,
    snakbar: false,
    typeForm: null,
    snakbarText: null,
    loading: true,
    options: {
      itemsPerPage: 5,
    },
    headers: null,
    type: null,
    search: null,
    selectedHeaders: [],
    headersMap: null,
    excelItems: [],
  }),
  computed: {
    proListKey() {
      return this.expireDateItemsColumnPersistKey;
    },
    proListLocked() {
      return this.expireDateItemsLockedColumns;
    },
    showSelect() {
      return !this.isMobile;
    },
    expireDateItemsColumnPersistKey() {
      return "entrepot-expire-date-items-columns";
    },
    expireDateItemsLockedColumns() {
      // Fixed sticky block: select + row # (no unpin/drag)
      var locked = ["actions"];
      return this.showSelect ? ["__select"].concat(locked) : locked;
    },
    expireDateItemsColumnPlugins() {
      // show-select inserts a leading checkbox <th>; pad visibleHeaders so
      // pin/resize/drag index mapping stays aligned with thead cells.
      var selectHeader = {
        value: "__select",
        text: "",
        width: 32,
        sortable: false,
        pinned: false,
        originalIndex: -1,
      };
      var visible = this.showSelect
        ? [selectHeader].concat(this.tableHeaders || [])
        : this.tableHeaders || [];
      return {
        headers: this.headers,
        visibleHeaders: visible,
        locked: this.expireDateItemsLockedColumns,
        anchor: "actions",
        minWidth: 60,
        persistKey: this.expireDateItemsColumnPersistKey,
      };
    },
    activeFilterChips() {
      var app = this;
      var has = function (v) {
        if (Array.isArray(v)) return v.length > 0;
        return v !== null && v !== undefined && v !== "" && v !== "0" && v !== 0;
      };
      var show = function (v) {
        if (Array.isArray(v)) {
          if (!v.length) return "";
          if (typeof v[0] === "string" || typeof v[0] === "number") {
            return v.length > 1 ? v[0] + " …" : String(v[0]);
          }
          return v.length > 1 ? String(v.length) : show(v[0]);
        }
        if (v && typeof v === "object") return v.name || v.text || v.label || "";
        return typeof v === "number" || typeof v === "string" ? String(v) : "";
      };
      var chips = [];
      if (has(app.expireDate)) {
        chips.push({
          key: "expireDate",
          label: app.$t("expireDateRang"),
          value: Array.isArray(app.expireDate)
            ? app.expireDate.filter(Boolean).join(" – ")
            : show(app.expireDate),
        });
      }
      [
        ["product", app.$t("product")],
        ["entrepot", app.$t("entrepot")],
        ["branch", app.$t("branch")],
      ].forEach(function (d) {
        if (has(app[d[0]])) {
          chips.push({ key: d[0], label: d[1], value: show(app[d[0]]) });
        }
      });
      return chips;
    },
    isMobile() {
      return this.$vuetify.breakpoint.name === "xs";
    },
    showHeaders() {
      var selected = this.selectedHeaders || [];
      if (!this.headers) return [];
      if (!selected.length) return this.headers;
      return this.headers.filter(function (h) {
        return selected.some(function (s) {
          return (s && s.value) === h.value || s === h;
        });
      });
    },
    totals() {
      var app = this;
      app.excelItems = [];
      return this.items.reduce(
        function (acc, d) {
          var inn = d.in ? parseFloat(d.in) : 0;
          var out = app.rowOut(d);
          acc.in += inn;
          acc.out += out;
          app.excelItems.push({
            id: d.id,
            type:
              d.head
                ? app.$t(d.head.type) + " " + app.$t("number") + " " + d.head.id
                : "",
            warehouse: d.entrepot ? d.entrepot.name : "",
            date: d.head ? d.head.date : "",
            expire_date: d.expire_date || "",
            product: d.product ? d.product.name : "",
            unit: d.unit ? d.unit.fame || d.unit.name : "",
            in: d.in,
            out: out,
            remain: app.rowRemain(d),
          });
          return acc;
        },
        { in: 0, out: 0 }
      );
    },
    mobileSummary() {
      return [
        { label: this.$t("in"), value: this.$numberFormat(this.totals.in, "number"), tone: "pos" },
        { label: this.$t("out"), value: this.$numberFormat(this.totals.out, "number"), tone: "neg" },
        {
          label: this.$t("remain"),
          value: this.formatRemain(this.totals.in - this.totals.out),
        },
      ];
    },
  },
  watch: {
    idProp() {
      this.getItems();
    },
    refId() {
      this.getItems();
    },
    model() {
      this.$emit("input", this.model);
    },
    selected() {
      this.$emit("input", this.selected.text);
      if (this.selected.length > 0) {
        this.model = this.selected[0].text;
        this.temp = this.selected[0].text;
      }
      this.dialog = false;
    },
    search() {
      var app = this;
      app.loading = true;
      app.page = 1;
      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        app.getItems();
      }, 1000);
    },
    options: {
      handler() {
        var app = this;
        if (!app.headers) return;
        app.sortBy = [];
        (app.options.sortBy || []).map(function (value) {
          var index = app.headers.findIndex(function (h) {
            return h.value === value;
          });
          if (index > -1 && app.headers[index].table) {
            app.sortBy.push(app.headers[index].table);
          }
        });
        this.getItems();
      },
      deep: true,
    },
  },
  beforeMount() {
    this.headers = [
      {
        text: this.$t("#"),
        align: "center",
        sortable: false,
        value: "actions",
        width: 42,
        class: "pl-action-col",
        cellClass: "pl-action-col",
      },
      {
        text: this.$t("product"),
        sortable: true,
        value: "product.name",
        table: "products.name",
      },
      {
        text: this.$t("type"),
        sortable: true,
        value: "head.type",
        table: "entrepot_head.type",
      },
      {
        text: this.$t("enterDate"),
        sortable: true,
        value: "head.date",
        table: "entrepot_head.date",
      },
      {
        text: this.$t("entrepot"),
        sortable: true,
        value: "entrepot.name",
        table: "entrepots.name",
      },
      {
        text: this.$t("expireDate"),
        sortable: true,
        value: "expire_date",
        table: "entrepot_article.expire_date",
      },
      {
        text: this.$t("countIn"),
        sortable: true,
        value: "in",
        table: "entrepot_article.in",
      },
      {
        text: this.$t("countOut"),
        sortable: true,
        value: "out",
        table: "entrepot_article.out",
      },
      {
        text: this.$t("countStock"),
        sortable: false,
        value: "remain",
      },
      {
        text: this.$t("unit"),
        sortable: false,
        value: "unit.fame",
      },
    ];
    this.headersMap = this.headers;
    this.selectedHeaders = this.headers;
    reorderPinnedHeaders(this.headers, {
      locked: this.expireDateItemsLockedColumns,
      anchor: "actions",
    });
    this.loadExpireDateItemsColumnSettings();
    this.productStatusItems = [
      { value: "active", text: this.$t("active") },
      { value: "deactive", text: this.$t("deactive") },
    ];
    if (this.input) {
      this.model = this.input;
    }
  },
  mounted() {
    var mStart = this.$moment();
    var mEnd = this.$moment().add(30, "day");
    if (this.$store.state.date_locale === "fa,en") {
      this.expireDate = [
        mStart.format("jYYYY/jMM/jDD"),
        mEnd.format("jYYYY/jMM/jDD"),
      ];
    }
    if (this.$store.state.date_locale === "en,fa") {
      this.expireDate = [
        mStart.format("YYYY/MM/DD"),
        mEnd.format("YYYY/MM/DD"),
      ];
    }
    this.getItems();
  },
  methods: {
    loadExpireDateItemsColumnSettings() {
      var app = this;
      if (!app.headers || !app.headers.length) return;
      loadColumnSettings(app, app.expireDateItemsColumnPersistKey).then(function (snapshot) {
        if (!snapshot || !snapshot.length) return;
        applyColumnSettings(app.headers, snapshot, {
          locked: app.expireDateItemsLockedColumns,
          anchor: "actions",
        });
        app.$forceUpdate();
      });
    },
    rowOut(item) {
      if (!item) return 0;
      if (item.out != null && item.out !== "") return parseFloat(item.out) || 0;
      if (item.sum_out != null && item.sum_out !== "") return parseFloat(item.sum_out) || 0;
      return 0;
    },
    rowRemain(item) {
      if (!item) return 0;
      if (item.remain != null && item.remain !== "") return parseFloat(item.remain) || 0;
      return (parseFloat(item.in) || 0) - this.rowOut(item);
    },
    formatRemain(n) {
      var v = parseFloat(n);
      if (!isFinite(v)) v = 0;
      return parseFloat(v.toFixed(10)).toLocaleString("en-US", {
        maximumFractionDigits: 10,
      });
    },
    canOpenDoc(item) {
      return (
        item &&
        item.head &&
        item.head.type !== "initial" &&
        item.head.type !== "production"
      );
    },
    expirePillClass(dateStr) {
      if (!dateStr) return "pl-pill--amber";
      try {
        var m = this.$moment(dateStr, [
          "jYYYY/jMM/jDD",
          "YYYY/MM/DD",
          "YYYY-MM-DD",
          "jYYYY-jMM-jDD",
        ]);
        if (!m.isValid()) return "pl-pill--amber";
        var today = this.$moment().startOf("day");
        var d = m.clone().startOf("day");
        if (d.isBefore(today)) return "pl-pill--red pl-pill--solid";
        if (d.diff(today, "days") <= 7) return "pl-pill--amber pl-pill--solid";
        return "pl-pill--green";
      } catch (e) {
        return "pl-pill--amber";
      }
    },
    applyFilters() {
      this.filter = false;
      this.page = 1;
      this.getItems();
    },
    resetFilters() {
      this.product = null;
      this.entrepot = null;
      this.branch = null;
      // keep default expire window — only clear product/warehouse/branch chips
      this.getItems();
    },
    clearFilterChip(key) {
      if (key === "expireDate") {
        this.expireDate = null;
      } else if (Object.prototype.hasOwnProperty.call(this.$data, key)) {
        this[key] = null;
      }
      this.getItems();
    },
    openDeleteDialog(item) {
      this.deleteItem = item;
      this.deletDialog = true;
    },
    edit(item) {
      if (!this.canOpenDoc(item)) return;
      this.$router.push({
        name: item.head.type + "Edit",
        params: { id: item.head.id },
      });
    },
    invoice(item) {
      if (item.head.type === "production") {
        this.$router.push({
          name: item.head.production.type + "Edit",
          params: { id: item.head.production.id },
        });
      } else {
        this.$router.push({
          name: item.head.invoice.type + "Edit",
          params: { id: item.head.invoice.uid },
        });
      }
    },
    getItems() {
      var app = this;
      app.loading = true;
      var productId = app.productProp ? app.productProp.id : null;
      productId = this.$route.params.id ? this.$route.params.id : productId;
      app.$axios
        .get(this.$endpoint + "productsreport", {
          params: {
            product_id: app.product ? app.product.id : null,
            productProp_id: productId,
            entrepotProp: !app.entrepot ? app.entrepotProp : null,
            entrepot: app.entrepot,
            search: app.search,
            page: app.options.page,
            refId: app.refId,
            branch: app.branch,
            idProp: app.idProp,
            type: app.type,
            report: "expireDateItems",
            sortBy: app.sortBy,
            status: app.productStatus,
            options: app.options,
            tag: app.tag,
            typeForm: app.typeForm,
            name: app.$route.name,
            expireDate: app.expireDate,
          },
        })
        .then(function (res) {
          res = res.data;
          app.items = res.data;
          app.totalItems = res.total;
          app.pageCount = res.last_page;
          app.from = res.from;
          if (app.options.page === 1) {
            app.sumAmount = 0;
            app.sumCount = 0;
          }
          app.items.map(function (value) {
            if (value.in > 0 || value.out > 0 || value.sum_out > 0) {
              var out = app.rowOut(value);
              if (value.in > 0) {
                app.sumAmount =
                  parseFloat(app.sumAmount) +
                  parseFloat(value.price || 0) * parseFloat(value.in);
                app.sumCount = parseFloat(app.sumCount) + parseFloat(value.in);
              } else {
                app.sumCount = parseFloat(app.sumCount) - out;
                app.sumAmount =
                  parseFloat(app.sumAmount) -
                  parseFloat(value.price || 0) * out;
              }
            }
            value.sum = app.sumAmount;
            value.sumCount = app.sumCount;
          });
        })
        .catch(function (err) {
          app.serverErrorHandeler(err);
        })
        .finally(function () {
          app.loading = false;
        });
    },
    remove() {
      var app = this;
      if (!app.deleteItem) return false;
      app.loading = true;
      app.$axios
        .delete(this.$endpoint + "invoice/" + this.deleteItem.id)
        .then(function () {
          app.deletDialog = false;
          app.deleteItem = null;
          app.getItems();
        })
        .catch(function (err) {
          app.deletDialog = false;
          app.serverErrorHandeler(err);
        })
        .finally(function () {
          app.loading = false;
        });
    },
    removegroup() {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "invoice", {
          params: {
            deleteItems: app.selected,
          },
        })
        .then(function () {
          app.deletDialog = false;
          app.deleteItem = null;
          app.selected = [];
          app.getItems();
        })
        .catch(function (err) {
          app.deletDialog = false;
          app.serverErrorHandeler(err);
        })
        .finally(function () {
          app.loading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/sass/design-system/list/pro-list";

::v-deep .v-data-table table thead tr th,
::v-deep .v-data-table table tbody tr td {
  white-space: nowrap;
}

::v-deep .v-data-table > .v-data-footer {
  flex-wrap: wrap;
}

button.pl-key,
button.pl-pill {
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  padding: 0;
}
button.pl-pill {
  padding: 0 6px;
  background: var(--cs, rgba(100, 116, 139, 0.1));
}

.theme--dark ::v-deep .app-list-page-totals {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.86);
}
.theme--dark ::v-deep .app-list-page-totals__label,
.theme--dark ::v-deep .app-list-page-totals__item--final .app-list-page-totals__key,
.theme--dark ::v-deep .app-list-page-totals__item--final .app-list-page-totals__val {
  color: rgba(255, 255, 255, 0.92);
}
.theme--dark ::v-deep .app-list-page-totals__item {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.78);
}
.theme--dark ::v-deep .app-list-page-totals__key {
  color: rgba(255, 255, 255, 0.5);
}
</style>
